import { QueryClient, defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'

const DB_NAME = 'tbc-pwa-query-cache-db'
const STORE_NAME = 'cache'
const CACHE_KEY = 'tbc-pwa-query-cache'
const PERSISTED_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 7
const PERSISTED_OFFERS_MAX_AGE = 1000 * 60 * 60 * 24
const GIFT_CARD_OFFERS_QUERY_KEY_PREFIX = ['giftcards', 'offers']
const ORDERS_QUERY_KEY_PREFIX = ['orders']
const USER_SCOPED_QUERY_KEY_PREFIXES = [
  ORDERS_QUERY_KEY_PREFIX,
  ['balances'],
  ['accessTokens'],
  ['giftcards', 'quote'],
] as const

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

const idbStorage = {
  getItem: async (key: string): Promise<string | null> => {
    try {
      const db = await openDB()
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      return new Promise((resolve, reject) => {
        const req = store.get(key)
        req.onsuccess = () => resolve(req.result ?? null)
        req.onerror = () => reject(req.error)
      })
    } catch {
      return null
    }
  },
  setItem: async (key: string, value: string): Promise<void> => {
    try {
      const db = await openDB()
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      return new Promise((resolve, reject) => {
        const req = store.put(value, key)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
      })
    } catch {}
  },
  removeItem: async (key: string): Promise<void> => {
    try {
      const db = await openDB()
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      return new Promise((resolve, reject) => {
        const req = store.delete(key)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
      })
    } catch {}
  },
}

const basePersister = createAsyncStoragePersister({
  storage: idbStorage,
  key: CACHE_KEY,
  throttleTime: 1000,
})

type PersistedClientSnapshot = Awaited<ReturnType<typeof basePersister.restoreClient>>
type PersistedQuerySnapshot = NonNullable<PersistedClientSnapshot>['clientState']['queries'][number]

function hasQueryKeyPrefix(queryKey: unknown, prefix: readonly string[]): boolean {
  if (!Array.isArray(queryKey) || queryKey.length < prefix.length) {
    return false
  }

  return prefix.every((segment, index) => queryKey[index] === segment)
}

function getPersistedQueryMaxAge(query: PersistedQuerySnapshot): number {
  if (hasQueryKeyPrefix(query.queryKey, GIFT_CARD_OFFERS_QUERY_KEY_PREFIX)) {
    return PERSISTED_OFFERS_MAX_AGE
  }

  return PERSISTED_CACHE_MAX_AGE
}

function shouldRestorePersistedQuery(query: PersistedQuerySnapshot, now = Date.now()): boolean {
  // Order payloads embed claim codes; never hydrate them, including snapshots
  // written by older releases that predate the dehydration filter.
  if (hasQueryKeyPrefix(query.queryKey, ORDERS_QUERY_KEY_PREFIX)) {
    return false
  }

  const dataUpdatedAt = query.state.dataUpdatedAt

  if (!Number.isFinite(dataUpdatedAt) || dataUpdatedAt <= 0) {
    return false
  }

  return now - dataUpdatedAt <= getPersistedQueryMaxAge(query)
}

export function filterPersistedClientByQueryAge(
  persistedClient: PersistedClientSnapshot,
  now = Date.now()
): PersistedClientSnapshot {
  if (!persistedClient) {
    return persistedClient
  }

  return {
    ...persistedClient,
    clientState: {
      ...persistedClient.clientState,
      queries: persistedClient.clientState.queries.filter((query) =>
        shouldRestorePersistedQuery(query, now)
      ),
    },
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 2,
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
    },
  },
})

export function clearUserScopedQueries(): void {
  for (const queryKey of USER_SCOPED_QUERY_KEY_PREFIXES) {
    queryClient.removeQueries({ queryKey })
  }
}

export async function clearPersistedQueryCache(): Promise<void> {
  await basePersister.removeClient()
}

export const persistOptions = {
  persister: {
    persistClient: basePersister.persistClient,
    restoreClient: async () => {
      const persistedClient = await basePersister.restoreClient()
      return filterPersistedClientByQueryAge(persistedClient)
    },
    removeClient: basePersister.removeClient,
  },
  maxAge: PERSISTED_CACHE_MAX_AGE,
  dehydrateOptions: {
    // Order payloads embed gift-card claim codes/barcodes; never write them to IndexedDB.
    shouldDehydrateQuery: (query: Query) =>
      defaultShouldDehydrateQuery(query) &&
      !hasQueryKeyPrefix(query.queryKey, ORDERS_QUERY_KEY_PREFIX),
  },
}
